/**
 * Resource tabs — <resource-tabs> custom element (no Shadow DOM).
 * Markup: .tab-container / .tab-button / .tab-content-container (live 1.18 + resource-tab.liquid).
 */
class ResourceTabs extends HTMLElement {
  /** @param {string | number} index */
  activateTab(index) {
    const tabIndex = String(index);
    const nav = this.querySelector('.tab-container');
    const panels = this.querySelectorAll('.tab-content-container');

    if (!nav || panels.length === 0) {
      return;
    }

    nav.querySelectorAll('.tab-button').forEach((button) => {
      const isSelected = button.dataset.index === tabIndex;
      button.classList.toggle('selected', isSelected);
      button.setAttribute('aria-selected', isSelected ? 'true' : 'false');
      button.tabIndex = isSelected ? 0 : -1;
    });

    panels.forEach((panel) => {
      const isActive = panel.dataset.index === tabIndex;

      if (isActive) {
        panel.removeAttribute('hidden');
        panel.classList.add('is-active');
      } else {
        panel.setAttribute('hidden', '');
        panel.classList.remove('is-active');
      }

      panel.setAttribute('aria-hidden', isActive ? 'false' : 'true');
    });

    this.dispatchEvent(
      new CustomEvent('resource-tab:change', {
        bubbles: true,
        detail: { index: tabIndex },
      })
    );
  }

  /**
   * Map a Theme Editor block element to a tab index (tab button, panel, or nested card).
   * @param {Element} blockEl
   * @returns {string | undefined}
   */
  #indexFromBlockElement(blockEl) {
    if (blockEl.matches('.tab-button')) {
      return blockEl.dataset.index;
    }

    if (blockEl.matches('.tab-content-container')) {
      return blockEl.dataset.index;
    }

    const panel = blockEl.closest('.tab-content-container');
    if (panel?.dataset.index) {
      return panel.dataset.index;
    }

    const button = blockEl.closest('.tab-button');
    if (button?.dataset.index) {
      return button.dataset.index;
    }

    return undefined;
  }

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    const button = event.target.closest('.tab-button');
    if (!button || !this.contains(button)) {
      return;
    }

    event.preventDefault();
    this.activateTab(button.dataset.index);
  };

  /** @param {KeyboardEvent} event */
  #onKeydown = (event) => {
    const nav = this.querySelector('.tab-container');
    if (!nav || !nav.contains(event.target)) {
      return;
    }

    const buttons = Array.from(nav.querySelectorAll('.tab-button'));
    const currentIndex = buttons.findIndex((btn) => btn.classList.contains('selected'));
    if (currentIndex < 0) {
      return;
    }

    let nextIndex = currentIndex;

    if (event.key === 'ArrowRight') {
      nextIndex = (currentIndex + 1) % buttons.length;
    } else if (event.key === 'ArrowLeft') {
      nextIndex = (currentIndex - 1 + buttons.length) % buttons.length;
    } else if (event.key === 'Home') {
      nextIndex = 0;
    } else if (event.key === 'End') {
      nextIndex = buttons.length - 1;
    } else {
      return;
    }

    event.preventDefault();
    const nextButton = buttons[nextIndex];
    this.activateTab(nextButton.dataset.index);
    nextButton.focus();
  };

  /** @param {Event} event */
  #onBlockSelect = (event) => {
    if (!(event.target instanceof Element) || !this.contains(event.target)) {
      return;
    }

    const index = this.#indexFromBlockElement(event.target);
    if (index != null) {
      this.activateTab(index);
    }
  };

  #activateInitial() {
    const nav = this.querySelector('.tab-container');
    const initial =
      nav?.querySelector('.tab-button.selected') || nav?.querySelector('.tab-button');

    if (initial?.dataset.index != null) {
      this.activateTab(initial.dataset.index);
    }
  }

  connectedCallback() {
    this.addEventListener('click', this.#onClick);
    this.addEventListener('keydown', this.#onKeydown);
    document.addEventListener('shopify:block:select', this.#onBlockSelect);
    this.#activateInitial();
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
    this.removeEventListener('keydown', this.#onKeydown);
    document.removeEventListener('shopify:block:select', this.#onBlockSelect);
  }
}

if (!window.customElements.get('resource-tabs')) {
  window.customElements.define('resource-tabs', ResourceTabs);
}
